import React, { useState } from "react";
import { CicdFaqs } from "../../../components/faqData.js";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const DockerContainers = () => {
  const [actIndex, setActIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActIndex(index === actIndex ? null : index);
  };
  const scrollToContact = () => {
    document
      .getElementById("contact-section")
      .scrollIntoView({ behavior: "smooth" });
  };

  const dockerServices = [
    {
    logo_images:"../assets/icons/docker/service-1.png",
      title: "Image Creation",
      description:
        "Build optimized Docker images with multi-stage builds, layer caching, and best practices to reduce image size and improve build performance. We create production-ready images with security scanning and vulnerability management.",
    },
    {
      logo_images:"../assets/icons/docker/service-2.png",
      title: "Container Orchestration",
      description:
        "Deploy and manage containers at scale using Kubernetes, Docker Swarm, or cloud-native orchestration platforms. We design container orchestration strategies for high availability, auto-scaling, and service discovery.",
    },
    {
      logo_images:"../assets/icons/docker/service-3.png",
      title: "Docker Compose",
      description:
        "Define and run multi-container Docker applications with Docker Compose. We create compose files for local development, testing environments, and production deployments with service dependencies and networking.",
    },
    {
      logo_images:"../assets/icons/docker/service-4.png",
      title: "Container Security",
      description:
        "Implement container security best practices including image scanning, runtime protection, secrets management, and compliance with security standards. We ensure containers are hardened and follow least-privilege principles.",
    },
    {
      logo_images:"../assets/icons/docker/service-5.png",
      title: "Registry (ECR, ACR, DockerHub)",
      description:
        "Set up and manage container registries across AWS ECR, Azure ACR, Google Container Registry, and Docker Hub. We configure automated builds, image versioning, access controls, and integration with CI/CD pipelines.",
    },
  ];

  const benefits = [
    "Consistent environments across development, testing, and production",
    "Faster application deployment and scaling with containerized workloads",
    "Resource efficiency through lightweight containerization vs. virtual machines",
    "Improved portability across different cloud platforms and on-premises",
    "Isolation and security with container-level boundaries",
    "Simplified dependency management and application packaging",
  ];

  const workflowSteps = [
    {
      title: "Container Strategy & Planning",
      description:
        "Assess your application architecture, identify containerization opportunities, and design containerization strategy aligned with your business goals and technical requirements.",
    },
    {
      title: "Dockerfile Optimization",
      description:
        "Create optimized Dockerfiles with multi-stage builds, minimal base images, and best practices to reduce image size, build time, and security vulnerabilities.",
    },
    {
      title: "Container Registry Setup",
      description:
        "Configure and integrate container registries (ECR, ACR, GCR, Docker Hub) with automated builds, image scanning, versioning, and access management policies.",
    },
    {
      title: "Orchestration & Deployment",
      description:
        "Deploy containers using Kubernetes, Docker Swarm, or cloud-native services with auto-scaling, load balancing, health checks, and rolling update strategies.",
    },
    {
      title: "CI/CD Integration",
      description:
        "Integrate container builds and deployments into CI/CD pipelines with automated testing, security scanning, and deployment workflows across environments.",
    },
    {
      title: "Monitoring & Optimization",
      description:
        "Implement container monitoring, logging, and performance optimization with tools like Prometheus, Grafana, and cloud-native monitoring services.",
    },
  ];

  const useCases = [
    {
      title: "Microservices Containerization",
      description:
        "Containerized a monolithic application into 30+ microservices using Docker, reducing deployment time from 4 hours to 15 minutes and enabling independent scaling of services based on demand.",
    },
    {
      title: "Multi-Cloud Container Strategy",
      description:
        "Deployed containerized applications across AWS ECS, Azure Container Instances, and GKE, achieving 99.99% availability with automated failover and workload distribution.",
    },
    {
      title: "Legacy Application Modernization",
      description:
        "Containerized legacy Java applications with Docker, enabling deployment to Kubernetes clusters and reducing infrastructure costs by 50% while improving deployment frequency by 10x.",
    },
    {
      title: "DevOps CI/CD Pipeline",
      description:
        "Built end-to-end containerized CI/CD pipelines with Docker, Jenkins, and Kubernetes, automating builds, testing, security scanning, and deployments across 5 environments.",
    },
  ];

  const whyChooseUs = [
    "Docker Certified Associates and Kubernetes experts with 10+ years of containerization experience",
    "End-to-end services from containerization strategy to production deployment and management",
    "Optimized Docker images reducing size by 40-60% and build time by 50%",
    "Security-first approach with automated vulnerability scanning and compliance",
    "Multi-platform expertise across Docker, Kubernetes, and cloud-native container services",
    "24/7 monitoring and support for containerized applications in production",
  ];

  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Cut deployment costs & time. Ship code 10x faster with our Accelerated Container Development services. Boost efficiency today. Get a Quote."
        />
        <meta name="copyright" content="codeglim" />
        <meta
          property="og:title"
          content="Ship Code 10x Faster with Accelerated Container Development | Ayanshtech Solutions"
        />
        <meta property="og:site_name" content="Ayanshtech Solutions" />
        <meta
          property="og:url"
          content="http://ayanshtechnology.com/devops-docker"
        />
        <meta
          property="og:description"
          content="Cut deployment costs & time. Ship code 10x faster with our Accelerated Container Development services. Boost efficiency today. Get a Quote."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="Ship Code 10x Faster with Accelerated Container Development | Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Cut deployment costs & time. Ship code 10x faster with our Accelerated Container Development services. Boost efficiency today. Get a Quote."
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <link
          rel="canonical"
          href="http://ayanshtechnology.com/devops-docker"
        />
        <title>
         Ship Code 10x Faster with Accelerated Container Development | Ayanshtech Solutions
        </title>
      </Helmet>
      <div className="flex flex-col items-center justify-center mb-6 p-6">
        {/* Hero Section */}
        <div className="py-5 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-3/4 lg:text-left p-4">
              <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">Ship Code 10x Faster with Accelerated Container Application Development</h1>
              <p className="sm:text-base md:text-lg lg:text-lg mb-3">
                Accelerate your application deployment and scaling with Docker
                containerization. Ayanshtech Solutions delivers comprehensive
                Docker and container services, helping you package applications,
                manage dependencies, and deploy consistently across any
                environment.
              </p>

              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Our containerization experts design, build, and optimize Docker
                images and containerized applications that are secure, efficient,
                and production-ready. From Dockerfile creation and image
                optimization to container orchestration and registry management,
                we help you leverage the full power of containerization.
              </p>

              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Whether you're containerizing legacy applications, building
                cloud-native microservices, or optimizing existing container
                deployments, our team ensures best practices, security
                compliance, and performance optimization at every step.
              </p>

              <button
                onClick={scrollToContact}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
              >
                Get Started
              </button>
            </div>

            <div className="flex w-full md:w-2/3 justify-center mb-12 lg:mb-0">
              <img
                src="../assets/images/devops-pages/docker-banner.png"
                alt="Docker & Containerization"
                className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* What is Containerization */}
        {/* <div className="mb-5 mt-10 p-4">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            What is Containerization
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Containerization is a lightweight virtualization technology that
            packages applications and their dependencies into portable,
            self-contained units called containers. Unlike traditional virtual
            machines, containers share the host operating system kernel while
            maintaining isolated runtime environments, resulting in faster
            startup times, lower resource overhead, and improved portability.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Docker is the industry-leading containerization platform that
            simplifies container creation, management, and deployment. With
            Docker, you can build applications once and run them consistently
            across development, testing, and production environments, eliminating
            the "it works on my machine" problem and accelerating software
            delivery cycles.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            At Ayanshtech, we help organizations adopt containerization to
            modernize applications, improve deployment efficiency, and enable
            cloud-native architectures. Our comprehensive Docker services cover
            everything from initial containerization to orchestration and
            production management.
          </p>
        </div> */}

        {/* Docker Services */}
        <div className="text-center mb-5 mt-12">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Docker Services
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Our comprehensive Docker services cover the entire containerization
            lifecycle, from image creation to production deployment and
            management.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {dockerServices.map((service, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                  
                >
                  <div className="flex items-center gap-2">
                    <div className="w-[60px] h-[60px]  ">
                      <img src={service.logo_images} className="w-full" alt="" />
                    </div>
                  <h3 className="text-lg font-bold">{service.title}</h3>
                  </div>
                  <p className="text-lg">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits of Containers */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Benefits of Containers
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Discover how containerization can transform your application
            deployment, improve efficiency, and accelerate your development
            lifecycle.
          </p>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 rounded-2xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              >
                <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Our Expert Workflow */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Our Expert Workflow
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Our proven containerization workflow ensures successful Docker
            adoption from strategy to production deployment.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {workflowSteps.map((step, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <h3 className="text-lg font-bold">{step.title}</h3>
                  <p className="text-lg">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Real Use Cases */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Real-World Use Cases
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            See how we've helped organizations achieve their containerization
            goals with Docker and container technologies.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <h3 className="text-lg font-bold">{useCase.title}</h3>
                  <p className="text-lg">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Why Choose Ayanshtech for Docker & Container Services
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Partner with Ayanshtech for containerization expertise that drives
              measurable business outcomes. Our proven methodology, certified
              engineers, and customer-first approach ensure successful Docker
              adoption and container deployments.
            </p>
          </div>

          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                >
                  <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                  <p className="text-lg text-gray-700">{reason}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={scrollToContact}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
            >
              Schedule a Free Docker Consultation Today!
            </button>
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto p-6 py-20 w-full">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-8">
            FAQs
          </h2>
          {CicdFaqs.map((faq, index) => (
            <div key={index} className="border-b">
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full text-left py-4 flex justify-between items-center focus:outline-none ${
                  actIndex === index
                    ? "bg-blue-600 text-white px-5 rounded-2xl"
                    : ""
                }`}
              >
                <span className="text-lg">{faq.question}</span>
                <span className="text-2xl">
                  {actIndex === index ? "-" : "+"}
                </span>
              </button>
              {actIndex === index && (
                <div className="p-4 text-gray-700">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div id="contact-section">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default DockerContainers;

